import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

/**
 * 7-step onboarding tour.
 *
 * Persistence policy (Lotto 6 #3):
 *   • Fresh account       → shown on first login
 *   • Every subsequent    → shown on every login until the user explicitly
 *     login                 taps "Non mostrare più"
 *   • "Non mostrare più"  → persisted on the server profile
 *                           (`PATCH /auth/me {tour_dont_show:true}`), so it
 *                           survives logout/login and device change
 *   • Salta / close (X)   → hides for the current session only, tour comes
 *                           back next time the user logs in
 *
 * The context-assistant "?" (rendered by ContextAssistant.jsx, fixed at the
 * right edge) remains available even after "Non mostrare più" is set — it is
 * a separate control and NOT gated by tour_dont_show.
 */
const SESSION_HIDE_KEY = "tc_tour_session_hidden_v2";

export default function OnboardingTour() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  const steps = [
    { titleKey: "tour_step1_title", descKey: "tour_step1_desc", path: null },
    { titleKey: "tour_step2_title", descKey: "tour_step2_desc", path: "/map" },
    { titleKey: "tour_step3_title", descKey: "tour_step3_desc", path: "/map" },
    { titleKey: "tour_step4_title", descKey: "tour_step4_desc", path: "/map" },
    { titleKey: "tour_step5_title", descKey: "tour_step5_desc", path: "/map" },
    { titleKey: "tour_step6_title", descKey: "tour_step6_desc", path: "/map" },
    { titleKey: "tour_step7_title", descKey: "tour_step7_desc", path: "/map" },
  ];

  useEffect(() => {
    if (!user) { setVisible(false); return; }
    // Account-level opt-out wins over any session state.
    if (user.tour_dont_show === true) { setVisible(false); return; }
    // Session-scoped hide: user clicked Salta / X in this session, we hide
    // until the session ends (tab closes) but the tour comes back at next
    // login on any device.
    let hidden = false;
    try { hidden = sessionStorage.getItem(SESSION_HIDE_KEY) === "1"; } catch {}
    if (!hidden) { setStep(0); setVisible(true); }
  }, [user]);

  // Manual re-open from the ContextAssistant "?" or the settings.
  useEffect(() => {
    const handler = () => { setStep(0); setVisible(true); };
    window.addEventListener("tc:open-tour", handler);
    return () => window.removeEventListener("tc:open-tour", handler);
  }, []);

  if (!visible) return null;
  const s = steps[step];

  // Session-scoped close (Salta / X): reappears at next login on any device.
  const hideForSession = () => {
    try { sessionStorage.setItem(SESSION_HIDE_KEY, "1"); } catch {}
    setVisible(false);
  };

  // Account-scoped permanent opt-out — persisted on the server profile.
  const dontShowAgain = async () => {
    try { await api.patch("/auth/me", { tour_dont_show: true }); refresh && refresh(); }
    catch { /* stay silent — worst case tour reappears next login, no data loss */ }
    // Also mark the current session so it doesn't reappear until refresh.
    try { sessionStorage.setItem(SESSION_HIDE_KEY, "1"); } catch {}
    setVisible(false);
  };

  const next = () => {
    if (step + 1 < steps.length) {
      const nxt = steps[step + 1];
      if (nxt.path && loc.pathname !== nxt.path) nav(nxt.path);
      setStep(step + 1);
    } else {
      // Finishing the tour without pressing "Don't show again" leaves the
      // preference intact: tour will come back next login.
      hideForSession();
    }
  };
  const prev = () => { if (step > 0) setStep(step - 1); };

  return (
    <div className="fixed inset-x-0 bottom-4 sm:bottom-6 z-[3000] px-4 pointer-events-none" data-testid="onboarding-tour">
      <div className="mx-auto max-w-md bg-white border border-emerald-200 rounded-2xl shadow-2xl p-5 pointer-events-auto relative fade-in">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0"><Sparkles size={16} /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">{t("tour_start")} · {step + 1}/{steps.length}</div>
            <div className="font-heading text-lg font-bold text-slate-900 mt-0.5" data-testid="tour-title">{t(s.titleKey)}</div>
            <div className="text-sm text-slate-600 mt-1 leading-relaxed" data-testid="tour-desc">{t(s.descKey)}</div>
          </div>
          <button data-testid="tour-close" onClick={hideForSession} aria-label={t("close")} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={14} /></button>
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Button data-testid="tour-prev" onClick={prev} disabled={step === 0} variant="outline" size="sm" className="rounded-full hover:bg-slate-100 hover:text-slate-900"><ChevronLeft size={14} className="mr-1" /> {t("tour_prev")}</Button>
          <Button data-testid="tour-next" onClick={next} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white">{step + 1 === steps.length ? t("tour_close") : t("tour_next")} <ChevronRight size={14} className="ml-1" /></Button>
          <button data-testid="tour-skip" onClick={hideForSession} className="text-xs text-slate-500 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100">{t("tour_skip")}</button>
          <button data-testid="tour-dont-show" onClick={dontShowAgain} className="text-xs text-slate-500 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100 ml-auto">{t("tour_dont_show")}</button>
        </div>
      </div>
    </div>
  );
}
